import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Edit, Settings, CreditCard, Bot, Calendar, Mail, User, Shield, ExternalLink, ArrowUpDown } from 'lucide-react';
import PortalLayout from '@/components/portal/PortalLayout';
// AdminRoute removed - AuthMiddleware handles all permission checking
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { UserChatbotsSection } from "@/components/admin/users/UserChatbotsSection";
import { UserStripeSection } from "@/components/admin/StripeUserManagement";
import { UserActivitySection } from "@/components/admin/users/UserActivitySection";
import { useAdminMutations } from "@/hooks/mutations/useAdminMutations";
import { getAdminUserList } from '@/api/admin';

export const UserDetailsPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('chatbots');
  const [showQuickRoleModal, setShowQuickRoleModal] = useState(false);
  
  const { updateRole } = useAdminMutations();

  // Fetch user data
  const { data: users = [], isLoading, error } = useQuery({
    queryKey: ['adminUserList'],
    queryFn: getAdminUserList,
  });

  const user = users.find((u) => u.id === userId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        <span className="ml-2">Loading user details...</span>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="text-center py-20">
        <p className="text-red-600">User not found or error loading user details</p>
        <Button onClick={() => navigate('/admin/users')} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Users
        </Button>
      </div>
    );
  }

  // Type assertion for user object to resolve TypeScript issues
  const typedUser = user as {
    id: string;
    email: string;
    name?: string;
    role: 'user' | 'admin';
    is_banned: boolean;
    subscription_status?: string;
    created_at: string;
    updated_at?: string;
    stripe_customer_id?: string;
    user_id?: string;
    current_period_end?: string;
  };

  const getInitials = (name: string, email: string) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return email.slice(0, 2).toUpperCase();
  };

  const getStatusColor = (status: string) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      past_due: 'bg-yellow-100 text-yellow-800',
      canceled: 'bg-red-100 text-red-800',
      trialing: 'bg-blue-100 text-blue-800',
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getRoleBadgeColor = (role: string) => {
    return role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800';
  };

  const handleQuickRoleSwitch = (newRole: 'user' | 'admin') => {
    updateRole.mutate({ userId: typedUser.id, role: newRole });
    setShowQuickRoleModal(false);
  };

  return (
    <PortalLayout>
      <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/admin/users')}
            className="p-2"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">User Details</h1>
            <p className="text-gray-600">Manage user profile, chatbots, and billing</p>
          </div>
        </div>
        <div className="flex space-x-2">
          
          
        </div>
      </div>

      {/* Enhanced User Profile Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">

        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Avatar and Basic Info */}
            <div className="flex flex-col items-center space-y-4 lg:items-start">
              <Avatar className="h-24 w-24">
                <AvatarFallback className="text-2xl">
                  {getInitials(typedUser.name || '', typedUser.email || '')}
                </AvatarFallback>
              </Avatar>
              <div className="text-center lg:text-left">
                <h2 className="text-2xl font-bold">{typedUser.name || 'No name set'}</h2>
                <p className="text-gray-600">{typedUser.email}</p>
                <div className="flex flex-wrap gap-2 mt-2 justify-center lg:justify-start">
                  <Dialog open={showQuickRoleModal} onOpenChange={setShowQuickRoleModal}>
                    <DialogTrigger asChild>
                      <button 
                        className={`${getRoleBadgeColor(typedUser.role)} cursor-pointer hover:opacity-80 transition-opacity inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2`}
                        title="Click to change role"
                      >
                        <Shield className="h-3 w-3 mr-1" />
                        {typedUser.role}
                      </button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle className="flex items-center space-x-2">
                          <ArrowUpDown className="h-5 w-5" />
                          <span>Quick Role Switch</span>
                        </DialogTitle>
                        <DialogDescription>
                          Change <strong>{typedUser.email}</strong>'s role quickly
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="text-sm text-gray-600">
                          Current role: <Badge className={getRoleBadgeColor(typedUser.role)}>{typedUser.role}</Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <Button
                            variant={typedUser.role === 'user' ? 'secondary' : 'outline'}
                            onClick={() => handleQuickRoleSwitch('user')}
                            disabled={typedUser.role === 'user' || updateRole.isPending}
                            className="flex items-center space-x-2 h-12"
                          >
                            <User className="h-4 w-4" />
                            <div className="text-left">
                              <div className="font-medium">User</div>
                              <div className="text-xs text-gray-500">Standard access</div>
                            </div>
                          </Button>
                          <Button
                            variant={typedUser.role === 'admin' ? 'secondary' : 'outline'}
                            onClick={() => handleQuickRoleSwitch('admin')}
                            disabled={typedUser.role === 'admin' || updateRole.isPending}
                            className="flex items-center space-x-2 h-12"
                          >
                            <Shield className="h-4 w-4" />
                            <div className="text-left">
                              <div className="font-medium">Admin</div>
                              <div className="text-xs text-gray-500">Full access</div>
                            </div>
                          </Button>
                        </div>
                        {typedUser.role === 'user' && (
                          <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
                            ⚠️ Promoting to admin will give full system access
                          </div>
                        )}
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setShowQuickRoleModal(false)}>
                          Cancel
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                  <Badge variant={typedUser.is_banned ? 'destructive' : 'secondary'}>
                    {typedUser.is_banned ? 'Banned' : 'Active'}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Account Details */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold border-b pb-2">Account Details</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <User className="h-4 w-4 text-gray-500" />
                  <div>
                    <p className="text-sm font-medium">User ID</p>
                    <p className="text-sm text-gray-600 font-mono">{typedUser.id}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <div>
                    <p className="text-sm font-medium">Account Created</p>
                    <p className="text-sm text-gray-600">{new Date(typedUser.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                {typedUser.updated_at && (
                  <div className="flex items-center space-x-3">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    <div>
                      <p className="text-sm font-medium">Last Updated</p>
                      <p className="text-sm text-gray-600">{new Date(typedUser.updated_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Subscription & Billing */}
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b pb-2">
                <h3 className="text-lg font-semibold">Subscription & Billing</h3>
                {typedUser?.stripe_customer_id && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(`https://dashboard.stripe.com/customers/${typedUser.stripe_customer_id}`, '_blank')}
                    className="flex items-center space-x-2"
                  >
                    <ExternalLink className="h-3 w-3" />
                    <span>Stripe Dashboard</span>
                  </Button>
                )}
              </div>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <CreditCard className="h-4 w-4 text-gray-500" />
                  <div>
                    <p className="text-sm font-medium">Subscription Status</p>
                    <Badge className={getStatusColor(typedUser.subscription_status)}>
                      {typedUser.subscription_status || 'none'}
                    </Badge>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <CreditCard className="h-4 w-4 text-gray-500" />
                  <div>
                    <p className="text-sm font-medium">Stripe Customer</p>
                    <p className="text-sm text-gray-600">
                      {typedUser.stripe_customer_id ? (
                        <span className="text-green-600">Connected</span>
                      ) : (
                        <span className="text-gray-500">Not connected</span>
                      )}
                    </p>
                  </div>
                </div>
                {typedUser.stripe_customer_id && (
                  <div className="flex items-center space-x-3">
                    <CreditCard className="h-4 w-4 text-gray-500" />
                    <div>
                      <p className="text-sm font-medium">Customer ID</p>
                      <p className="text-xs text-gray-600 font-mono">
                        {typedUser.stripe_customer_id}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for different sections */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="chatbots" className="flex items-center space-x-2">
            <Bot className="h-4 w-4" />
            <span>Chatbots</span>
          </TabsTrigger>
          <TabsTrigger value="billing" className="flex items-center space-x-2">
            <CreditCard className="h-4 w-4" />
            <span>Billing</span>
          </TabsTrigger>
          <TabsTrigger value="activity" className="flex items-center space-x-2">
            <Calendar className="h-4 w-4" />
            <span>Activity</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="chatbots" className="space-y-4">
          <UserChatbotsSection userId={typedUser.user_id || typedUser.id} />
        </TabsContent>

        <TabsContent value="billing" className="space-y-4">
          <UserStripeSection user={{
            id: typedUser.id,
            email: typedUser.email,
            name: typedUser.name || 'No name set',
            stripe_customer_id: typedUser.stripe_customer_id,
            subscription_status: typedUser.subscription_status,
            current_period_end: typedUser.current_period_end
          }} />
        </TabsContent>

        <TabsContent value="activity" className="space-y-4">
          <UserActivitySection userId={typedUser.user_id || typedUser.id} />
        </TabsContent>
        </Tabs>
      </div>
    </PortalLayout>
  );
};

export default UserDetailsPage;
